import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, X, AlertCircle, CheckCircle, FileText, Archive } from 'lucide-react';
import axios from 'axios';
import { Button, FormField, Input, Select, Textarea, FormSection, FormGrid, FileUploadManager, FileUpload, StatusBadge } from '../common/ui';
import DeviceAutocomplete from '../common/DeviceAutocomplete';
import { useAuth } from '../../contexts/AuthContext';
import RepairActivity from './RepairActivity';
import RepairChat from './RepairChat';
import RepairActionButtons from './components/RepairActionButtons';
import { normalizeSerialNumber, validateSerialNumber } from '../../utils/normalizeSerialNumber';
import { NON_REPAIRABLE_DEVICE_STATUSES, formatDeviceStatus } from '../../utils/statusUtils';
import { printPdfBlob } from '../../utils/pdfUtils';

const RepairNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const repairHistoryRef = useRef();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [loadingWooCommerceData, setLoadingWooCommerceData] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  
  // Determine if we're in create or edit mode
  const isCreateMode = !id;
  
  // Repair data
  const [repairData, setRepairData] = useState({
    id: '',
    serial_number: '',
    product_type: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_return_address: '',
    help_scout_link: '',
    acceptance_notes: '',
    warranty_repair: false,
    temporary_import: false,
    report_to_team_required: false,
    shipped_items: [],
    shipped_items_other: '',
    notify_customer: true,
    // Additional fields for existing repairs
    device_id: '',
    created_by: '',
    assigned_to: '',
    status: 'draft',
    internal_notes: '',
    
    reported_date: '',
    started_date: '',
    completed_date: '',
    final_resolution: '',
    woocommerce_link: ''
  });

  // Reference data
  const [devices, setDevices] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchReferenceData(),
          isCreateMode ? null : fetchRepair()
        ]);
        
        // Handle pre-selected device from navigation state (for create mode)
        if (isCreateMode && location.state?.preselectedDevice) {
          const device = location.state.preselectedDevice;
          setRepairData(prev => ({
            ...prev,
            device_id: device.id,
            serial_number: device.serial_number,
            product_type: device.model
          }));
        }
      } catch (error) {
        console.error('Error initializing:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [id, isCreateMode, location.state]);

  const fetchReferenceData = async () => {
    try {
      const [devicesRes, usersRes, productsRes] = await Promise.all([
        axios.get('/api/devices'),
        axios.get('/api/users/basic'),
        axios.get('/api/device-types')
      ]);

      const devicesData = Array.isArray(devicesRes.data?.devices)
        ? devicesRes.data.devices
        : Array.isArray(devicesRes.data)
          ? devicesRes.data
          : [];
      const usersData = Array.isArray(usersRes.data?.users)
        ? usersRes.data.users
        : Array.isArray(usersRes.data)
          ? usersRes.data
          : [];
      const productsData = Array.isArray(productsRes.data)
        ? productsRes.data
        : Array.isArray(productsRes.data?.products)
          ? productsRes.data.products
          : [];

      setDevices(devicesData);
      setUsers(usersData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      throw error;
    }
  };

  const fetchRepair = async () => {
    try {
      const response = await axios.get(`/api/repairs/${id}`);
      const repair = response.data;
      
      // Format dates for input fields
      const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toISOString().slice(0, 16);
      };

      // For existing repairs, populate the new fields with data from device if available
      const device = devices.find(d => d.id === repair.device_id);
      
      setRepairData({
        ...repair,
        serial_number: device?.serial_number || repair.device_serial || '',
        product_type: device?.model || repair.device_model || '',
        customer_name: repair.customer_name || '',
        customer_email: repair.customer_email || '',
        customer_phone: repair.customer_phone || '',
        customer_return_address: repair.customer_return_address || '',
        acceptance_notes: repair.acceptance_notes || '',
        warranty_repair: repair.warranty_repair || false,
        temporary_import: repair.temporary_import || false,
        report_to_team_required: repair.report_to_team_required || false,
        shipped_items: repair.shipped_items || [],
        shipped_items_other: repair.shipped_items_other || '',
        notify_customer: repair.notify_customer !== undefined ? repair.notify_customer : true,
        help_scout_link: repair.help_scout_link || '',
        internal_notes: repair.internal_notes || '',
        final_resolution: repair.final_resolution || '',
        // Convert stored URLs to order IDs for easier editing
        woocommerce_link: urlsToOrderIds(repair.woocommerce_link || ''),
        reported_date: formatDate(repair.reported_date),
        started_date: formatDate(repair.started_date),
        completed_date: formatDate(repair.completed_date),
        created_by: repair.created_by || '',
        assigned_to: repair.assigned_to || ''
      });

    } catch (error) {
      console.error('Error fetching repair:', error);
      throw error;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for WooCommerce Order field - allow free typing
    if (name === 'woocommerce_link') {
      // Check if input looks like a partial URL (contains "post=" or "page=wc-orders" but not a full URL)
      const trimmedValue = value.trim();
      if ((trimmedValue.includes('post=') || trimmedValue.includes('page=wc-orders')) && !trimmedValue.startsWith('http')) {
        setRepairData(prev => ({
          ...prev,
          [name]: value
        }));
        // Don't show error here, let Load Data button handle it
        return;
      }
    }
    
    setRepairData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Convert a single order ID or URL to a full WooCommerce URL
  const toWooCommerceUrl = (input) => {
    let trimmed = input.trim();
    if (!trimmed) return null;
    
    // Strip leading # if present (e.g., "#148000" -> "148000")
    if (trimmed.startsWith('#')) {
      trimmed = trimmed.slice(1);
    }
    
    // If it's just a number, convert to URL
    if (/^\d+$/.test(trimmed)) {
      return `https://b2b.naviter.com/wp-admin/admin.php?page=wc-orders&action=edit&id=${trimmed}`;
    }
    
    // If it's already a URL, return as-is
    if (trimmed.startsWith('http')) {
      return trimmed;
    }
    
    return null;
  };

  // Parse input with multiple order IDs/URLs separated by various delimiters
  const parseWooCommerceInput = (input) => {
    if (!input) return '';
    
    // Split by common delimiters: comma, semicolon, space, newline
    const parts = input.split(/[,;\s\n]+/).filter(Boolean);
    
    // Convert each part to a full URL
    const urls = parts
      .map(part => toWooCommerceUrl(part))
      .filter(Boolean);
    
    // Join with commas for storage
    return urls.join(',');
  };

  // Extract order ID from a WooCommerce URL
  const extractWooCommerceId = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      const postId = urlObj.searchParams.get('post');
      if (postId && /^\d+$/.test(postId)) {
        return postId;
      }
      // HPOS format: admin.php?page=wc-orders&action=edit&id=123
      if (urlObj.searchParams.get('page') === 'wc-orders') {
        const hposId = urlObj.searchParams.get('id');
        if (hposId && /^\d+$/.test(hposId)) {
          return hposId;
        }
      }
    } catch (e) {
      // Try regex fallback
      const match = url.match(/post=(\d+)/);
      if (match) return match[1];
      if (url.includes('page=wc-orders')) {
        const hposMatch = url.match(/[?&]id=(\d+)/);
        if (hposMatch) return hposMatch[1];
      }
    }
    return null;
  };

  // Convert stored URLs back to just order IDs for easier editing
  const urlsToOrderIds = (urlsString) => {
    if (!urlsString) return '';
    const urls = urlsString.split(',').map(u => u.trim()).filter(Boolean);
    const ids = urls.map(url => {
      const id = extractWooCommerceId(url);
      return id || url; // fallback to original if can't extract
    });
    return ids.join(' ');
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setRepairData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleShippedItemsChange = (e) => {
    const { value, checked } = e.target;
    setRepairData(prev => {
      const newShippedItems = checked
        ? [...prev.shipped_items, value]
        : prev.shipped_items.filter(item => item !== value);
      
      // If "Other" checkbox is unchecked, clear the shipped_items_other field
      const shouldClearOther = value === 'Other' && !checked;
      
      return {
        ...prev,
        shipped_items: newShippedItems,
        shipped_items_other: shouldClearOther ? '' : prev.shipped_items_other
      };
    });
  };

  const handleSerialNumberChange = (value) => {
    setRepairData(prev => ({
      ...prev,
      serial_number: value
    }));
    
    if (errors.serial_number) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.serial_number;
        return newErrors;
      });
    }
  };

  const handleDeviceSelect = (device) => {
    // Determine product type - prefer product_name, fallback to model
    // Ensure it matches one of the available products in the dropdown
    let productType = device.product_name || device.model || '';
    
    // If we have products loaded, check if the product type matches an option
    // If not, try to find a match (case-insensitive)
    if (products.length > 0 && productType) {
      const exactMatch = products.find(p => p.name === productType);
      if (exactMatch) {
        productType = exactMatch.name;
      } else {
        // Try case-insensitive match
        const caseInsensitiveMatch = products.find(p => 
          p.name.toLowerCase() === productType.toLowerCase()
        );
        if (caseInsensitiveMatch) {
          productType = caseInsensitiveMatch.name;
        }
        // If no match found, use the original value anyway
        // (user might need to manually select if product doesn't exist in dropdown)
      }
    }
    
    setRepairData(prev => ({
      ...prev,
      serial_number: device.serial_number,
      product_type: productType
    }));

    // Clear errors for serial number and product type
    setErrors(prev => {
      const newErrors = { ...prev };
      if (newErrors.serial_number) delete newErrors.serial_number;
      if (newErrors.product_type) delete newErrors.product_type;
      return newErrors;
    });
  };

  const handleSubmit = async (e, status = null, options = {}) => {
    const { skipNavigation = false } = options;
    e.preventDefault();
    setErrors({});
    setError('');
    
    const newErrors = {};

    // Normalize serial number before validation
    if (isCreateMode && repairData.serial_number) {
      repairData.serial_number = normalizeSerialNumber(repairData.serial_number);
    }

    // Validation for create mode
    if (isCreateMode) {
      if (!repairData.serial_number) {
        newErrors.serial_number = 'Serial number is required';
      } else {
        const serialCheck = validateSerialNumber(repairData.serial_number);
        if (!serialCheck.valid) newErrors.serial_number = serialCheck.error;
      }
      if (!repairData.product_type) newErrors.product_type = 'Product type is required';
      if (!repairData.customer_name) newErrors.customer_name = 'Customer name is required';
      if (!repairData.acceptance_notes) newErrors.acceptance_notes = 'Acceptance notes are required';
      
      // Validate email format if provided
      if (repairData.customer_email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(repairData.customer_email)) {
          newErrors.customer_email = 'Please enter a valid email address';
        }
      }
      
      // Validate that if "Other" is checked, shipped_items_other must be provided
      if (repairData.shipped_items.includes('Other') && (!repairData.shipped_items_other || repairData.shipped_items_other.trim() === '')) {
        newErrors.shipped_items_other = 'Please specify what other items were shipped';
      }
    } else {
      // Validation for edit mode
      if (!repairData.device_id) newErrors.device_id = 'Device is required';
      if (!repairData.created_by) newErrors.created_by = 'Created by is required';
      
      // Validate email format if email is provided
      if (repairData.customer_email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(repairData.customer_email)) {
          newErrors.customer_email = 'Please enter a valid email address';
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please fix the errors below');
      
      // Scroll to the first missing field
      setTimeout(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const element = document.querySelector(`[name="${firstErrorKey}"]`) || document.querySelector(`[id="${firstErrorKey}"]`); // Fallback to ID for non-standard inputs
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (element.focus) element.focus();
        } else {
            // Fallback for DeviceAutocomplete which doesn't have a simple input name prop exposed on top div
            if (firstErrorKey === 'serial_number') {
                const autocompleteInput = document.querySelector('input[placeholder="Enter device serial number"]');
                 if (autocompleteInput) {
                    autocompleteInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    autocompleteInput.focus();
                 }
            }
        }
      }, 100);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const submitData = { ...repairData };
      
      if (isCreateMode) {
        // For new repairs, we need to find or create the device first
        let deviceId = null;
        
        // Try to find existing device by serial number via API
        try {
          const deviceResponse = await axios.get(`/api/devices/serial/${repairData.serial_number}`);
          if (NON_REPAIRABLE_DEVICE_STATUSES.includes(deviceResponse.data.status)) {
            const statusLabel = formatDeviceStatus(deviceResponse.data.status).toLowerCase();
            const message = `This device is ${statusLabel} and cannot be sent to repair`;
            setErrors({ serial_number: message });
            setError(message);
            return;
          }
          deviceId = deviceResponse.data.id;
        } catch (deviceError) {
          // Device doesn't exist, create new device
          if (deviceError.response?.status === 404) {
            try {
              const deviceResponse = await axios.post('/api/devices', {
                serial_number: repairData.serial_number,
                model: repairData.product_type,
                status: 'registered'
              });
              deviceId = deviceResponse.data.id;
            } catch (createError) {
              console.error('Error creating device:', createError);
              setError(`Failed to create device: ${createError.response?.data?.error || createError.message}`);
              return;
            }
          } else {
            console.error('Error checking device existence:', deviceError);
            setError(`Failed to check device: ${deviceError.response?.data?.error || deviceError.message}`);
            return;
          }
        }
        
        // Use the logged-in user as the creator
        const newRepairData = {
          device_id: deviceId,
          created_by: user?.id,
          status: status || 'waiting_for_repair', // Use provided status or default to waiting_for_repair
          customer_name: repairData.customer_name,
          customer_email: repairData.customer_email,
          customer_phone: repairData.customer_phone,
          customer_return_address: repairData.customer_return_address,
          acceptance_notes: repairData.acceptance_notes,
          warranty_repair: repairData.warranty_repair,
          temporary_import: repairData.temporary_import,
          report_to_team_required: repairData.report_to_team_required,
          shipped_items: repairData.shipped_items,
          shipped_items_other: repairData.shipped_items_other,
          notify_customer: repairData.notify_customer,
          help_scout_link: repairData.help_scout_link,
          // Convert order IDs to full URLs for storage
          woocommerce_link: parseWooCommerceInput(repairData.woocommerce_link)
        };
        
        const createResponse = await axios.post('/api/repairs', newRepairData);
        const newRepairId = createResponse?.data?.id;
        
        // If internal note was provided, add it as the first comment
        if (newRepairId && repairData.internal_notes && repairData.internal_notes.trim()) {
          try {
            await axios.post(`/api/repairs/${newRepairId}/comments`, {
              comment_text: repairData.internal_notes.trim(),
              created_by: user?.id
            });
          } catch (commentErr) {
            console.error('Error adding initial comment:', commentErr);
            // We continue even if comment fails, but maybe log a warning
          }
        }

        // If user selected files during creation, upload them now and link to the new repair
        if (newRepairId && pendingFiles.length > 0) {
          try {
            const formData = new FormData();
            pendingFiles.forEach(file => {
              formData.append('files', file);
            });
            await axios.post(`/api/attachments/repairs/${newRepairId}/upload`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(`Repair created successfully and ${pendingFiles.length} attachment(s) uploaded`);
          } catch (uploadErr) {
            console.error('Error uploading attachments for new repair:', uploadErr);
            const errorMessage = uploadErr.response?.data?.error || 'Repair created, but failed to upload attachments';
            setError(errorMessage);
            // Stop here to let user see the error and retry saving if needed
            return;
          }
        } else {
          setSuccess('Repair created successfully');
        }
        
        // Navigate to the matching status tab after a short delay (unless skipNavigation)
        if (!skipNavigation) {
          const targetStatus = status || 'waiting_for_repair';
          setTimeout(() => {
            navigate(`/repairs?status=${targetStatus}`);
          }, 50);
        }
        
        return newRepairId;
        
      } else {
        // Format dates for submission
        const formatDateForSubmission = (dateString) => {
          if (!dateString) return null;
          return new Date(dateString).toISOString();
        };

        submitData.reported_date = formatDateForSubmission(submitData.reported_date);
        submitData.started_date = formatDateForSubmission(submitData.started_date);
        submitData.completed_date = formatDateForSubmission(submitData.completed_date);

        // Remove non-database fields
        delete submitData.serial_number;
        delete submitData.product_type;
        // Remove deprecated fields if present
        delete submitData.priority;
        delete submitData.repair_type;
        delete submitData.cost;
        delete submitData.issue_description;
        delete submitData.external_notes;
        // Rename fields
        if (submitData.reported_by !== undefined) {
          submitData.created_by = submitData.reported_by;
          delete submitData.reported_by;
        }
        if (submitData.assigned_technician !== undefined) {
          submitData.assigned_to = submitData.assigned_technician;
          delete submitData.assigned_technician;
        }
        if (submitData.resolution !== undefined) {
          submitData.final_resolution = submitData.resolution;
          delete submitData.resolution;
        }

          // Validate final_resolution is required for certain statuses
        if (submitData.status !== undefined) {
          const statusesRequiringResolution = ['repaired', 'waiting_for_payment', 'ready_for_shipping', 'completed'];
          if (statusesRequiringResolution.includes(submitData.status)) {
            const finalResolution = submitData.final_resolution || repairData.final_resolution;
            if (!finalResolution || finalResolution.trim() === '') {
              setErrors(prev => ({ ...prev, final_resolution: 'Final resolution is required when setting repair to this status' }));
              setError('Final resolution is required');
              return;
            }
          }
          
          // Validate delivery_method is required for completed status
          if (submitData.status === 'completed') {
            const deliveryMethod = submitData.delivery_method || repairData.delivery_method;
            if (!deliveryMethod || deliveryMethod.trim() === '') {
               // Note: delivery_method field not present in current form, assuming logic is kept for future or hidden fields
               // If it were present, we'd set error here
            }
            
            // If delivery_method is "other", validate that delivery_method_other is provided
            if (deliveryMethod === 'other') {
              const deliveryMethodOther = submitData.delivery_method_other || repairData.delivery_method_other;
              if (!deliveryMethodOther || deliveryMethodOther.trim() === '') {
                 // Same here
              }
          }
        }
        }
        
        // Validate that if "Other" is checked, shipped_items_other must be provided
        const shippedItems = submitData.shipped_items || repairData.shipped_items || [];
        const shippedItemsOther = submitData.shipped_items_other || repairData.shipped_items_other || '';
        if (shippedItems.includes('Other') && (!shippedItemsOther || shippedItemsOther.trim() === '')) {
          setErrors(prev => ({ ...prev, shipped_items_other: 'Please specify what other items were shipped' }));
          setError('Please fix the errors below');
          return;
        }

        // Add performed_by for history tracking
        submitData.performed_by = user?.id || null;
        
        // Convert order IDs to full URLs for storage
        if (submitData.woocommerce_link !== undefined) {
          submitData.woocommerce_link = parseWooCommerceInput(submitData.woocommerce_link);
        }
        
        await axios.put(`/api/repairs/${id}`, submitData);
        setSuccess('Repair updated successfully');
        
        // Refresh repair history if status changed
        if (repairHistoryRef.current) {
          repairHistoryRef.current.refresh();
        }
        
        return id;
      }
      
    } catch (error) {
      console.error('Error saving repair:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save repair';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Extract order ID from a single WooCommerce URL
  const extractOrderIdFromUrl = (url) => {
    if (!url) return null;
    
    let trimmed = url.trim();
    
    // Strip leading # if present (e.g., "#148000" -> "148000")
    if (trimmed.startsWith('#')) {
      trimmed = trimmed.slice(1);
    }
    
    // If it's just a number, return it directly
    if (/^\d+$/.test(trimmed)) {
      return trimmed;
    }
    
    // Try to extract from URL
    try {
      const urlObj = new URL(trimmed);
      const postParam = urlObj.searchParams.get('post');
      if (postParam && /^\d+$/.test(postParam)) {
        return postParam;
      }
      // HPOS format: admin.php?page=wc-orders&action=edit&id=123
      if (urlObj.searchParams.get('page') === 'wc-orders') {
        const hposId = urlObj.searchParams.get('id');
        if (hposId && /^\d+$/.test(hposId)) {
          return hposId;
        }
      }
    } catch (e) {
      // Try regex fallback
      const match = trimmed.match(/post=(\d+)/);
      if (match) {
        return match[1];
      }
      if (trimmed.includes('page=wc-orders')) {
        const hposMatch = trimmed.match(/[?&]id=(\d+)/);
        if (hposMatch) {
          return hposMatch[1];
        }
      }
    }
    return null;
  };

  // Handler for loading WooCommerce order data
  const handleLoadWooCommerceData = async () => {
    const woocommerceLink = repairData.woocommerce_link?.trim();
    
    if (!woocommerceLink) {
      setError('WooCommerce Order URL is required');
      return;
    }

    // Handle multiple order IDs/URLs - split by common delimiters
    // User input could be "146086 146087" or "146086, 146087" etc.
    const parts = woocommerceLink.split(/[,;\s\n]+/).filter(Boolean);
    const firstPart = parts[0].trim();
    
    // Extract order ID from the first part
    const orderId = extractOrderIdFromUrl(firstPart);
    
    if (!orderId) {
      setError('Could not extract order ID. Please enter order numbers (e.g., "146086 146087") or full URLs');
      return;
    }

    // Check if there are multiple orders
    const hasMultipleOrders = parts.length > 1;

    try {
      setLoadingWooCommerceData(true);
      setError('');
      setSuccess('');

      const response = await axios.get(`/api/b2b/orders/${orderId}/details`);

      if (response.data.success) {
        // Replace existing values with fetched data
        setRepairData(prev => ({
          ...prev,
          customer_name: response.data.customer_name || '',
          customer_email: response.data.customer_email || '',
          customer_phone: response.data.customer_phone || '',
          customer_return_address: response.data.customer_return_address || ''
        }));
        const successMsg = hasMultipleOrders 
          ? `Customer data loaded from first order (#${orderId})`
          : 'Customer data loaded successfully from WooCommerce order';
        setSuccess(successMsg);
      } else {
        setError(response.data.error || 'Failed to load order data');
      }
    } catch (error) {
      console.error('Error loading WooCommerce data:', error);
      
      // Check if it's a 404 error
      if (error.response?.status === 404) {
        setError(`Can't find order ${orderId}`);
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to load order data';
        setError(errorMessage);
      }
    } finally {
      setLoadingWooCommerceData(false);
    }
  };

  // Handler for Save button (saves as draft)
  const handleSave = async () => {
    // Validate serial number is required
    if (!repairData.serial_number) {
      setErrors(prev => ({ ...prev, serial_number: 'Serial number is required' }));
      setError('Please fix the errors below');
      return;
    }
    
    const fakeEvent = { preventDefault: () => {} };
    await handleSubmit(fakeEvent, 'draft');
  };

  // Helper function to generate and download PDF for a given repair ID
  const generatePDFForRepair = async (repairId) => {
    setGeneratingPDF(true);
    
    try {
      // Call the backend API to generate PDF server-side (download=true returns PDF blob)
      const response = await axios.post(`/api/repairs/${repairId}/generate-pdf?download=true`, {}, {
        responseType: 'blob' // Important: tell axios to expect binary data
      });

      // Check if the response is actually an error (JSON) instead of a PDF
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const blob = response.data;
      await printPdfBlob(blob);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `repair-${repairId}-report.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      return { success: true, filename };
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Handle blob error responses
      let errorMessage = 'Failed to generate PDF';
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // Keep default error message
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      return { success: false, error: errorMessage };
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Handler for Print PDF button (saves with waiting_for_repair status and generates PDF)
  const handlePrintPDF = async () => {
    if (!repairData.serial_number) {
      setErrors(prev => ({ ...prev, serial_number: 'Serial number is required' }));
      setError('Please fix the errors below');
      return;
    }
    
    const fakeEvent = { preventDefault: () => {} };
    
    // Save with waiting_for_repair status, but don't navigate yet
    const repairId = await handleSubmit(fakeEvent, 'waiting_for_repair', { skipNavigation: true });
    
    // If save failed (no repair ID returned), stop here
    if (!repairId) {
      return;
    }
    
    // Generate and download the PDF
    const pdfResult = await generatePDFForRepair(repairId);
    
    if (pdfResult.success) {
      setSuccess(`Repair saved and PDF generated: ${pdfResult.filename}`);
      setTimeout(() => {
        navigate('/repairs?status=waiting_for_repair');
      }, 50);
    } else {
      // PDF failed but repair was saved - show error and stay on page
      setError(`Repair saved but PDF generation failed: ${pdfResult.error}. You can try printing the PDF later.`);
    }
  };
  
  // Handler for Clarification Required (saves + generates PDF)
  const handleClarificationRequired = async () => {
    if (!repairData.serial_number) {
      setErrors(prev => ({ ...prev, serial_number: 'Serial number is required' }));
      setError('Please fix the errors below');
      return;
    }
    
    const fakeEvent = { preventDefault: () => {} };
    const repairId = await handleSubmit(fakeEvent, 'clarification_required', { skipNavigation: true });
    if (!repairId) return;

    const pdfResult = await generatePDFForRepair(repairId);
    if (pdfResult.success) {
      setSuccess(`Repair saved and PDF generated: ${pdfResult.filename}`);
      setTimeout(() => {
        navigate('/repairs?status=clarification_required');
      }, 50);
    } else {
      setError(`Repair saved but PDF generation failed: ${pdfResult.error}. You can try printing the PDF later.`);
    }
  };

  // Handler for Send to Backlog (saves + generates PDF)
  const handleSendToBacklog = async () => {
    if (!repairData.serial_number) {
      setErrors(prev => ({ ...prev, serial_number: 'Serial number is required' }));
      setError('Please fix the errors below');
      return;
    }
    
    const fakeEvent = { preventDefault: () => {} };
    const repairId = await handleSubmit(fakeEvent, 'backlog', { skipNavigation: true });
    if (!repairId) return;

    const pdfResult = await generatePDFForRepair(repairId);
    if (pdfResult.success) {
      setSuccess(`Repair saved and PDF generated: ${pdfResult.filename}`);
      setTimeout(() => {
        navigate('/repairs?status=backlog');
      }, 50);
    } else {
      setError(`Repair saved but PDF generation failed: ${pdfResult.error}. You can try printing the PDF later.`);
    }
  };

  // Removed attachment handlers - now using FileUploadManager
  /*const fetchAttachments = async () => {
    if (isCreateMode || !id) return;
    
    if (!user) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      setLoadingAttachments(true);
      console.log('Fetching attachments for repair ID:', id);
      console.log('User:', user);
      console.log('Authorization header:', axios.defaults.headers.common['Authorization']);
      console.log('Token from localStorage:', localStorage.getItem('token'));
      
      const response = await axios.get(`/api/attachments/repairs/${id}`);
      console.log('Attachments response:', response.data);
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed, redirecting to login');
        navigate('/login');
      }
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (isCreateMode) {
      setError('Please save the repair first before uploading files');
      return;
    }

    if (!user) {
      console.log('User not authenticated for upload');
      navigate('/login');
      return;
    }

    try {
      console.log('Uploading files for repair ID:', id);
      console.log('Files to upload:', files);
      console.log('Authorization header:', axios.defaults.headers.common['Authorization']);
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(`/api/attachments/repairs/${id}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload response:', response.data);
      setSuccess(`${files.length} file(s) uploaded successfully`);
      await fetchAttachments(); // Refresh attachments list
    } catch (error) {
      console.error('Error uploading files:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed during upload, redirecting to login');
        navigate('/login');
        return;
      }
      
      const errorMessage = error.response?.data?.error || 'Failed to upload files';
      setError(errorMessage);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`/api/attachments/repairs/${id}/files/${attachmentId}`);
      setSuccess('Attachment deleted successfully');
      await fetchAttachments(); // Refresh attachments list
    } catch (error) {
      console.error('Error deleting attachment:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete attachment';
      setError(errorMessage);
    }
  };

  const handleDownloadAttachment = (attachmentId, filename) => {
    window.open(`/api/attachments/repairs/${id}/files/${attachmentId}`, '_blank');
  };*/

  const handleGeneratePDF = async () => {
    if (isCreateMode) {
      setError('Please save the repair first before generating PDF');
      return;
    }

    try {
      setGeneratingPDF(true);
      setError('');
      setSuccess('');

      // Call the backend API to generate PDF server-side
      const response = await axios.post(`/api/repairs/${id}/generate-pdf`, {}, {
        responseType: 'blob' // Important: tell axios to expect binary data
      });

      // Check if the response is actually an error (JSON) instead of a PDF
      // Error responses will have content-type: application/json
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const blob = response.data;
      await printPdfBlob(blob);

      const contentDisposition = response.headers['content-disposition'];
      let filename = `repair-${id}-report.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      setSuccess(`PDF report generated successfully: ${filename}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Handle blob error responses
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          setError(errorData.error || 'Failed to generate PDF');
        } catch (e) {
          setError('Failed to generate PDF');
        }
      } else {
        setError(error.response?.data?.error || 'Failed to generate PDF');
      }
    } finally {
      setGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-naviter-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading repair data...</p>
        </div>
      </div>
    );
  }

  const getActionButtons = () => {
    const buttons = {};
    
    // Back button - always shown
    buttons.backButton = (
      <Button
        onClick={handleCancel}
        variant="ghost"
        size="xs"
        icon={ArrowLeft}
        className="flex-shrink-0 px-0"
      >
        Back
      </Button>
    );
    
    // Action buttons - Save is always included
    buttons.actionButtons = [
      <Button
        key="save"
        onClick={handleSave}
        variant="secondary"
        size="sm"
        icon={Save}
        loading={saving}
      >
        Save
      </Button>
    ];
    
    // For draft repairs (or new repairs), show additional buttons
    if (isCreateMode || repairData.status === 'draft') {
      buttons.actionButtons.push(
        <Button
          key="clarification"
          onClick={handleClarificationRequired}
          variant="secondary"
          size="sm"
          icon={AlertCircle}
          loading={saving || generatingPDF}
        >
          Clarification Required
        </Button>,
        <Button
          key="backlog"
          onClick={handleSendToBacklog}
          variant="secondary"
          size="sm"
          icon={Archive}
          loading={saving || generatingPDF}
        >
          Backlog
        </Button>
      );
      
      buttons.ctaButton = (
        <Button
          onClick={handlePrintPDF}
          variant="primary"
          size="md"
          icon={FileText}
          loading={saving || generatingPDF}
        >
          Waiting for repair
        </Button>
      );
    }
    
    return buttons;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-3">
          {/* Header */}
          <div className="space-y-3">
            {/* Title and status */}
            <div className="flex items-center gap-3">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {isCreateMode ? 'New repair' : `Repair #${id}`}
              </h1>
              {!isCreateMode && (
                <StatusBadge status={repairData.status} variant="repair" />
              )}
            </div>

            {/* PDF Generation Button */}
            {!isCreateMode && (
              <div>
                <Button
                  data-testid="generate-pdf-button"
                  variant="secondary"
                  onClick={handleGeneratePDF}
                  icon={FileText}
                  loading={generatingPDF}
                >
                  Print PDF
                </Button>
              </div>
            )}
            
            {/* Action Buttons */}
            <RepairActionButtons {...getActionButtons()} />
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex">
                <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
                <div className="ml-2">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex">
                <CheckCircle className="h-4 w-4 text-green-400 mt-0.5" />
                <div className="ml-2">
                  <div className="text-sm text-green-700">{success}</div>
                </div>
              </div>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-3">
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-4 space-y-3">
              {isCreateMode ? (
                /* New Repair Form */
                <>
                  <FormSection title="Device Information">
                    <FormGrid columns={2} gap={3}>
                      <FormField label="Serial Number" required error={errors.serial_number} size="sm">
                        <DeviceAutocomplete
                          value={repairData.serial_number}
                          onChange={handleSerialNumberChange}
                          onDeviceSelect={handleDeviceSelect}
                          placeholder="Enter device serial number"
                          required
                          error={!!errors.serial_number}
                          size="sm"
                          disableNonRepairable
                        />
                      </FormField>

                      <FormField label="Product Type" required error={errors.product_type} size="sm">
                        <Select
                          name="product_type"
                          value={repairData.product_type}
                          onChange={handleInputChange}
                          placeholder="Select product type"
                          required
                          error={!!errors.product_type}
                          size="sm"
                        >
                          {products.map(product => (
                            <option key={product.id} value={product.name}>
                              {product.name}
                            </option>
                          ))}
                        </Select>
                      </FormField>
                    </FormGrid>
                  </FormSection>

                  <FormSection title="Acceptance Notes">
                    <div className="space-y-2">
                      <FormField label="Acceptance Notes" required error={errors.acceptance_notes} size="sm">
                        <Textarea
                          name="acceptance_notes"
                          value={repairData.acceptance_notes}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Describe the reason why device is here"
                          required
                          error={!!errors.acceptance_notes}
                          size="sm"
                        />
                      </FormField>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="warranty_repair"
                            name="warranty_repair"
                            checked={repairData.warranty_repair}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="warranty_repair" className="text-xs font-medium text-gray-700">
                            Warranty repair
                          </label>
                        </div>

                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="temporary_import"
                            name="temporary_import"
                            checked={repairData.temporary_import}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="temporary_import" className="text-xs font-medium text-gray-700">
                            Temporary import
                          </label>
                        </div>

                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="report_to_team_required"
                            name="report_to_team_required"
                            checked={repairData.report_to_team_required}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="report_to_team_required" className="text-xs font-medium text-gray-700">
                            Report to the team
                          </label>
                        </div>
                      </div>

                      <FormField label="Items shipped with device" size="sm">
                        <div className="grid grid-cols-3 gap-1.5">
                          {['Antenna', 'Box', 'Carry bag', 'Jacket', 'SD Card', 'SIM card', 'USB Cable', 'Wall Charger', 'Other'].map(item => (
                            <div key={item} className="flex items-center space-x-1.5">
                              <input
                                type="checkbox"
                                id={`shipped_item_${item}`}
                                value={item}
                                checked={repairData.shipped_items.includes(item)}
                                onChange={handleShippedItemsChange}
                                className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                              />
                              <label htmlFor={`shipped_item_${item}`} className="text-xs text-gray-700">
                                {item}
                              </label>
                            </div>
                          ))}
                        </div>
                        {repairData.shipped_items.includes('Other') && (
                          <div className="mt-1.5">
                            <label htmlFor="shipped_items_other" className="block text-xs font-medium text-gray-700 mb-0.5">
                              Specify other items shipped <span className="text-red-500">*</span>
                            </label>
                            <Textarea
                              id="shipped_items_other"
                              name="shipped_items_other"
                              value={repairData.shipped_items_other}
                              onChange={handleInputChange}
                              placeholder="Enter what other items were shipped with the device..."
                              rows={2}
                              required
                              className="w-full"
                              error={!!errors.shipped_items_other}
                              size="sm"
                            />
                            {errors.shipped_items_other && <p className="mt-1 text-xs text-red-600">{errors.shipped_items_other}</p>}
                          </div>
                        )}
                      </FormField>
                    </div>
                  </FormSection>

                  <FormSection title="Customer Information">
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        <FormField label="HelpScout Conversation" size="sm">
                          <Input
                            type="url"
                            name="help_scout_link"
                            value={repairData.help_scout_link}
                            onChange={handleInputChange}
                            placeholder="https://..."
                            size="sm"
                          />
                        </FormField>

                        <FormField label="WooCommerce Order(s)" size="sm">
                          <div className="flex gap-1.5">
                            <Input
                              type="text"
                              name="woocommerce_link"
                              value={repairData.woocommerce_link}
                              onChange={handleInputChange}
                              placeholder="Order IDs or URLs (e.g., 146086 146087)"
                              className="flex-1"
                              size="sm"
                            />
                            <Button
                              onClick={handleLoadWooCommerceData}
                              disabled={!repairData.woocommerce_link?.trim() || loadingWooCommerceData}
                              loading={loadingWooCommerceData}
                              variant="secondary"
                              size="xs"
                            >
                              Load Data
                            </Button>
                          </div>
                        </FormField>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                        <FormField label="Customer Name" required error={errors.customer_name} size="sm">
                          <Input
                            name="customer_name"
                            value={repairData.customer_name}
                            onChange={handleInputChange}
                            placeholder="Enter customer name"
                            required
                            error={!!errors.customer_name}
                            size="sm"
                          />
                        </FormField>

                        <FormField label="Customer Email" error={errors.customer_email} size="sm">
                          <Input
                            type="email"
                            name="customer_email"
                            value={repairData.customer_email}
                            onChange={handleInputChange}
                            placeholder="Enter customer email"
                            error={!!errors.customer_email}
                            size="sm"
                          />
                        </FormField>

                        <FormField label="Customer Phone" size="sm">
                          <Input
                            type="tel"
                            name="customer_phone"
                            value={repairData.customer_phone}
                            onChange={handleInputChange}
                            placeholder="Enter customer phone"
                            size="sm"
                          />
                        </FormField>
                      </div>
                      
                      <div>
                        <FormField label="Customer Return Address" size="sm">
                          <Textarea
                            name="customer_return_address"
                            value={repairData.customer_return_address}
                            onChange={handleInputChange}
                            rows={2}
                            placeholder="Enter customer return address"
                            size="sm"
                          />
                        </FormField>
                      </div>

                      <div className="pt-1.5 border-t border-gray-200">
                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="notify_customer"
                            name="notify_customer"
                            checked={repairData.notify_customer}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="notify_customer" className="text-xs font-medium text-gray-700">
                            Send email notifications to customer
                          </label>
                        </div>
                        <p className="mt-0.5 ml-5 text-xs text-gray-500">
                          When enabled, the customer will receive email notifications when the device is received and when the repair is completed.
                        </p>
                      </div>
                    </div>
                  </FormSection>
                </>
              ) : (
                /* Edit Existing Repair Form */
                <>
                  <FormSection title="Device Information">
                    <FormGrid columns={2} gap={3}>
                      <FormField label="Serial Number" helpText="From linked device" size="sm">
                        <Input
                          name="serial_number"
                          value={repairData.serial_number}
                          disabled
                          className="bg-gray-50"
                          size="sm"
                        />
                      </FormField>

                      <FormField label="Product Type" helpText="From linked device" size="sm">
                        <Input
                          name="product_type"
                          value={repairData.product_type}
                          disabled
                          className="bg-gray-50"
                          size="sm"
                        />
                      </FormField>
                    </FormGrid>
                  </FormSection>

                  <FormSection title="Customer Information">
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5">
                        <FormField label="Customer Name" size="sm">
                          <Input
                            name="customer_name"
                            value={repairData.customer_name}
                            onChange={handleInputChange}
                            placeholder="Enter customer name"
                            size="sm"
                          />
                        </FormField>

                        <FormField label="Customer Email" size="sm">
                          <Input
                            type="email"
                            name="customer_email"
                            value={repairData.customer_email}
                            onChange={handleInputChange}
                            placeholder="Enter customer email"
                            size="sm"
                          />
                        </FormField>

                        <FormField label="Customer Phone" size="sm">
                          <Input
                            type="tel"
                            name="customer_phone"
                            value={repairData.customer_phone}
                            onChange={handleInputChange}
                            placeholder="Enter customer phone"
                            size="sm"
                          />
                        </FormField>

                        <FormField label="HelpScout Conversation" size="sm">
                          <Input
                            type="url"
                            name="help_scout_link"
                            value={repairData.help_scout_link}
                            onChange={handleInputChange}
                            placeholder="https://..."
                            size="sm"
                          />
                        </FormField>
                      </div>

                      <div>
                        <FormField label="Customer Return Address" size="sm">
                          <Textarea
                            name="customer_return_address"
                            value={repairData.customer_return_address}
                            onChange={handleInputChange}
                            rows={2}
                            placeholder="Enter customer return address"
                            size="sm"
                          />
                        </FormField>
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="Acceptance Notes">
                    <div className="space-y-2">
                      <FormField label="Acceptance Notes" size="sm">
                        <Textarea
                          name="acceptance_notes"
                          value={repairData.acceptance_notes}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Describe the reason why device is here"
                          size="sm"
                        />
                      </FormField>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="warranty_repair"
                            name="warranty_repair"
                            checked={repairData.warranty_repair}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="warranty_repair" className="text-xs font-medium text-gray-700">
                            Warranty repair
                          </label>
                        </div>

                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="temporary_import"
                            name="temporary_import"
                            checked={repairData.temporary_import}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="temporary_import" className="text-xs font-medium text-gray-700">
                            Temporary import
                          </label>
                        </div>

                        <div className="flex items-center space-x-1.5">
                          <input
                            type="checkbox"
                            id="report_to_team_required"
                            name="report_to_team_required"
                            checked={repairData.report_to_team_required}
                            onChange={handleCheckboxChange}
                            className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                          />
                          <label htmlFor="report_to_team_required" className="text-xs font-medium text-gray-700">
                            Report to the team
                          </label>
                        </div>
                      </div>

                      <FormField label="Items shipped with device" size="sm">
                        <div className="grid grid-cols-3 gap-1.5">
                          {['Antenna', 'Box', 'Carry bag', 'Jacket', 'SD Card', 'SIM card', 'USB Cable', 'Wall Charger', 'Other'].map(item => (
                            <div key={item} className="flex items-center space-x-1.5">
                              <input
                                type="checkbox"
                                id={`shipped_item_${item}`}
                                value={item}
                                checked={repairData.shipped_items.includes(item)}
                                onChange={handleShippedItemsChange}
                                className="h-3.5 w-3.5 text-naviter-blue focus:ring-naviter-blue border-gray-300 rounded"
                              />
                              <label htmlFor={`shipped_item_${item}`} className="text-xs text-gray-700">
                                {item}
                              </label>
                            </div>
                          ))}
                        </div>
                      </FormField>
                    </div>
                  </FormSection>

                  <FormSection title="Customer visible notes">
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5">
                        <FormField label="Device" required helpText="Cannot be changed" error={errors.device_id} size="sm">
                          <Select
                            name="device_id"
                            value={repairData.device_id || ''}
                            onChange={handleInputChange}
                            disabled={true}
                            required
                            error={!!errors.device_id}
                            size="sm"
                          >
                            {devices.map(device => (
                              <option key={device.id} value={device.id}>
                                {device.serial_number} - {device.model}
                              </option>
                            ))}
                          </Select>
                        </FormField>

                        <FormField label="Created by" required error={errors.created_by} size="sm">
                          <Select
                            name="created_by"
                            value={repairData.created_by || ''}
                            onChange={handleInputChange}
                            placeholder="Select user"
                            required
                            error={!!errors.created_by}
                            size="sm"
                          >
                            {/* Inactive users only show when already saved on this repair */}
                            {users
                              .filter(user => user.is_active !== false || String(user.id) === String(repairData.created_by))
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.name} ({user.email}){user.is_active === false ? ' (inactive)' : ''}
                                </option>
                              ))}
                          </Select>
                        </FormField>

                        <FormField label="Status" size="sm">
                          <Select
                            name="status"
                            value={repairData.status}
                            onChange={handleInputChange}
                            size="sm"
                          >
                            <option value="backlog">Backlog</option>
                            <option value="waiting_for_repair">Waiting for repair</option>
                            <option value="clarification_required">Clarification required</option>
                            <option value="waiting_for_dev_input">Waiting for dev input</option>
                            <option value="waiting_for_customer_response">Waiting for customer response</option>
                            <option value="work_in_progress">Work in progress</option>
                            <option value="repaired">Repaired</option>
                            <option value="ready_for_shipping">Ready for shipping</option>
                            <option value="waiting_for_payment">Waiting for payment</option>
                            <option value="completed">Completed</option>
                          </Select>
                        </FormField>

                        <FormField label="WooCommerce Link(s)" size="sm">
                          <Input
                            type="text"
                            name="woocommerce_link"
                            value={repairData.woocommerce_link}
                            onChange={handleInputChange}
                            placeholder="Order IDs or URLs (e.g., 146086 146087)"
                            size="sm"
                          />
                        </FormField>
                      </div>

                      <div>
                        <FormField label="Final resolution" error={errors.final_resolution} size="sm">
                          <Textarea
                            name="final_resolution"
                            value={repairData.final_resolution}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Describe how the issue was resolved..."
                            error={!!errors.final_resolution}
                            size="sm"
                          />
                        </FormField>
                      </div>
                    </div>
                  </FormSection>
                </>
              )}
                </div>
              </div>

              {/* Action Buttons - Bottom (outside form sections, in gray area) */}
              <div>
                <RepairActionButtons {...getActionButtons()} />
              </div>
              
              {/* Activity Section - Only show for existing repairs (outside form, in gray area) */}
              {!isCreateMode && (
                <div className="mt-3">
                  <RepairActivity 
                    ref={repairHistoryRef} 
                    deviceId={repairData.device_id} 
                    repairId={id}
                  />
                </div>
              )}
            </div>

            {/* Right Column - Internal Notes & Attachments */}
            <div className="lg:col-span-1 space-y-3 sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pb-8 custom-scrollbar">
              {/* Attachments */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Attachments</h3>
                  {isCreateMode ? (
                    <div className="space-y-4">
                      <FileUpload
                        onFilesSelected={(files) => setPendingFiles(files)}
                        disabled={saving}
                        maxFiles={10}
                        maxFileSize={50}
                        acceptMobileCapture={true}
                        showPreview={true}
                        compact={true}
                      />
                      {pendingFiles.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">Selected files will be uploaded after you Save or Create Repair.</p>
                      )}
                    </div>
                  ) : (
                    <FileUploadManager
                      entityType="repairs"
                      entityId={id}
                      compact={true}
                      disabled={saving}
                    />
                  )}
                </div>
              </div>

              {/* Internal Notes */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-4">
                  {isCreateMode ? (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Internal Notes</h3>
                      <div className="space-y-3">
                        <FormField label="Initial Comment" helpText="This will be added as the first comment in the internal notes">
                          <Textarea
                            name="internal_notes"
                            value={repairData.internal_notes}
                            onChange={handleInputChange}
                            rows={3}
                            placeholder="Add an initial comment..."
                          />
                        </FormField>
                      </div>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <RepairChat 
                        repairId={id} 
                        disabled={saving} 
                        onCommentAdded={() => repairHistoryRef.current?.refresh()} 
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepairNew;